import React, { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { RealPropertyModel } from '../../../../../../../../typings/api/skymap/rest/v1/project';
import { consequetivePairIterator, pairIterator } from '../../../../../../utils/generators';
import { joinIfDefined, toUpperFirst } from '../../../../../../utils/text/text';
import { AssetManager } from '../../../../../../viewer/asset-manager';
import { CoordinateManager } from '../../../../../../viewer/coordinate-manager';
import { Design } from '../../../../../../viewer/elements/design';
import { useComponentStatus } from '../../../../../hooks/use-component-status';
import { Button } from '../../../../button/button';
import { Dialog } from '../../../../dialog/dialog';
import { InfoBox } from '../../../../info-box/info-box';
import { Stack } from '../../../../stack/stack';

class EmptyBoundaryError extends Error {}

type ExportError = 'emptyBoundary' | 'unknown';

type Props = {
  item: RealPropertyModel;
  onClose: () => void;
};

export const ExportRealPropertyDialog = (props: Props) => {
  const { t } = useTranslation();
  const { setStatus, isLoading } = useComponentStatus();
  const [error, setError] = useState<ExportError>();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog
      closeButtonRef={closeButtonRef}
      closeIcon={!isLoading}
      closeOnDimmerClick={!isLoading}
      width={500}
      onClose={props.onClose}
    >
      {{
        header: t('onDemand.dialogs.export.title', { ns: 'components' }),
        content: (
          <Stack spacing={0.5}>
            <InfoBox color="yellow">
              <Trans
                components={{ bold: <strong /> }}
                i18nKey="onDemand.dialogs.export.info"
                ns="components"
                values={{
                  propertyName: joinIfDefined(' ', [props.item.municipality, props.item.name]),
                }}
              />
            </InfoBox>

            {error === 'emptyBoundary' && (
              <InfoBox color="red">
                {t('onDemand.dialogs.export.emptyBoundary', { ns: 'components' })}
              </InfoBox>
            )}

            {error === 'unknown' && (
              <InfoBox color="red">
                {t('onDemand.dialogs.export.error', { ns: 'components' })}
              </InfoBox>
            )}
          </Stack>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={isLoading}
                loading={isLoading}
                variant="contained"
                onClick={async () => {
                  try {
                    setError(undefined);
                    setStatus('loading');
                    await createDesignFromRealPropertyModel(props.item);
                    props.onClose();
                  } catch (err) {
                    setError(err instanceof EmptyBoundaryError ? 'emptyBoundary' : 'unknown');
                    setStatus('error');
                  }
                }}
              >
                {t('onDemand.dialogs.export.exportToDesign', { ns: 'components' })}
              </Button>

              <Button
                disabled={isLoading}
                ref={closeButtonRef}
                variant="text"
                onClick={props.onClose}
              >
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

/**
 * A boundary can be a multipolygon, i.e. several rings. Keep every ring that forms a polygon
 * (at least 3 points of [longitude, latitude] pairs) so one malformed part does not discard the rest.
 */
function getValidBoundaryPolygons(item: RealPropertyModel) {
  return item.wgs84LatLongBoundaryPolygons.filter((x) => x.length >= 6 && x.length % 2 === 0);
}

async function createDesignFromRealPropertyModel(item: RealPropertyModel) {
  const polygons = getValidBoundaryPolygons(item);

  if (polygons.length === 0) {
    throw new EmptyBoundaryError('The property has no valid boundary polygon');
  }

  const asset = new Design({
    name: `Fastighetsgräns - ${item.name}, ${toUpperFirst(item.municipality ?? '-')}`,
    visible: false,
    wgs84: {
      lat: polygons[0][1],
      lng: polygons[0][0],
    },
    MAS: 0,
  });

  const assetPosition = getLocalCoordFromWgs84CoordinateArray(polygons[0]);

  polygons.forEach((flatWgs84Coords: number[]) => {
    const pointIds = [...pairIterator(flatWgs84Coords)].map((coordinate) => {
      const position = getLocalCoordFromWgs84CoordinateArray(coordinate).sub(assetPosition);
      return asset.designNode.addPoint(position).id;
    });

    for (const [a, b] of consequetivePairIterator(pointIds, true)) {
      asset.designNode.addLineSegment(a, b);
    }
  });

  asset.calculateLocalPosition();
  asset.designNode.refresh();

  // If save to S3 fails, remove geometry from asset list.
  // todo: Don't add geometry to asset list (addAsset) as a requirement to be save to S3.
  try {
    AssetManager.instance.addAsset(asset);
    await asset.saveGeometryToS3();
    AssetManager.instance.select(asset);
  } catch (err) {
    AssetManager.instance.remove(asset);
    throw err;
  }
}

function getLocalCoordFromWgs84CoordinateArray(wgs84Coordinate: number[]) {
  return CoordinateManager.getInstance().wgs84CoordsToLocal({
    latitude: wgs84Coordinate[1],
    longitude: wgs84Coordinate[0],
    height: 0,
  });
}
